import { useMemo } from "react";
import {
  IssueTemplate,
  IssueRootCause,
  PermittedAction,
} from "@adsk/acc-issues-types";
import { useGetLevelOneUserShouldIncludeMemberFn } from "@adsk/acc-issues-common/hooks/data/useGetLevelOneUserShouldIncludeMemberFn";
import { useGroupedProjectMembersAsOptions } from "@adsk/acc-issues-common/hooks/data/useGroupedProjectMembersAsOptions";
import { getCurrentUserPermittedActionsForNewIssue } from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";
import { getIssueRootCausesEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueRootCauses";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getFlatOptions } from "@adsk/acc-issues-common/hooks/dropdown/useSelectedOption";

const useGetIsValidAssigneeOxygenId = (): {
  getIsValidAssigneeOxygenId: (oxygenId: string) => boolean;
} => {
  const permittedActions = useSelector(
    getCurrentUserPermittedActionsForNewIssue
  );
  const levelOneUserShouldIncludeMemberFn =
    useGetLevelOneUserShouldIncludeMemberFn();
  const shouldIncludeMemberFn = !permittedActions.includes(
    PermittedAction.AssignAll
  )
    ? levelOneUserShouldIncludeMemberFn
    : undefined;
  const groupedOptions = useGroupedProjectMembersAsOptions({
    shouldIncludeMemberFn,
    shouldIncludeEmpty: true,
  });
  // get an object with all valid assignees as keys.
  // faster than checking a list when there are tons of options,
  // and easier to debug than a Set
  const validOxygenIdMap = useMemo(() => {
    const validOptionIds: Record<string, boolean> = {};
    const flatOptions = getFlatOptions(groupedOptions);
    flatOptions.forEach(option => {
      validOptionIds[option.value] = true;
    });
    return validOptionIds;
  }, [groupedOptions]);

  const getIsValidAssigneeOxygenId = (oxygenId: string): boolean => {
    return !!validOxygenIdMap[oxygenId];
  };
  return { getIsValidAssigneeOxygenId };
};

/**
 *
 * The template refers to uids of certain objects which may or may not be active, or may be deleted,
 * or the current user may not have permission to access.
 *
 * For those objects, we do not want them to affect the attributes of issues created from the template.
 *
 * This applies to root cause and assignee, in particular
 *
 * (note that since type is required, we do not try to modify it... templates with inactive types will still be created)
 */
export const useGetTemplateTransformedForCurrentUser = () => {
  const rootCauseEntitiesMap = useSelector(getIssueRootCausesEntitiesMap);

  const { getIsValidAssigneeOxygenId } = useGetIsValidAssigneeOxygenId();

  const getTemplateTransformedForCurrentContext = ({
    template,
  }: {
    template: IssueTemplate;
  }): IssueTemplate => {
    let transformedTemplate = { ...template };
    if (transformedTemplate.issueAttributes.rootCauseId) {
      const rootCause = rootCauseEntitiesMap[
        transformedTemplate.issueAttributes.rootCauseId
      ] as IssueRootCause;
      if (!rootCause || !rootCause.isActive) {
        // this object is treated as read only in typescript so we need to re-create it.
        transformedTemplate = {
          ...transformedTemplate,
          issueAttributes: {
            ...transformedTemplate.issueAttributes,
            rootCauseId: undefined,
          },
        };
      }
    }
    if (transformedTemplate.issueAttributes.assigneeId) {
      const isAssigneeValid = getIsValidAssigneeOxygenId(
        transformedTemplate.issueAttributes.assigneeId
      );
      if (!isAssigneeValid) {
        transformedTemplate = {
          ...transformedTemplate,
          issueAttributes: {
            ...transformedTemplate.issueAttributes,
            assigneeId: undefined,
            assignedToType: undefined,
          },
        };
      }
    }
    return transformedTemplate;
  };
  return getTemplateTransformedForCurrentContext;
};
